import { Component, Show, createSignal, onCleanup, onMount } from "solid-js";
import { A } from "@solidjs/router";

type ActivationState = "checking" | "active" | "pending" | "signed-out";

const CheckoutSuccess: Component = () => {
  const [state, setState] = createSignal<ActivationState>("checking");
  let timer: number | undefined;

  const checkMembership = async (attempt = 0) => {
    try {
      const response = await fetch("/api/auth/me", { credentials: "include", cache: "no-store" });
      if (response.status === 401) {
        setState("signed-out");
        return;
      }
      if (response.ok && (await response.json()).is_paid) {
        setState("active");
        window.dispatchEvent(new Event("rrp:auth"));
        return;
      }
    } catch {
      // A short webhook delay or transient request failure is safe to retry.
    }

    if (attempt < 9) {
      timer = window.setTimeout(() => void checkMembership(attempt + 1), 1500);
    } else {
      setState("pending");
    }
  };

  onMount(() => void checkMembership());
  onCleanup(() => timer && window.clearTimeout(timer));

  return (
    <section class="checkout-success">
      <div class="success-mark" aria-hidden="true">✓</div>
      <div class="page-eyebrow">CHECKOUT COMPLETE</div>
      <Show when={state() === "active"}>
        <h1>Your Pro access is ready.</h1>
        <p>Your subscription is active and the complete mathematical analysis platform is now unlocked.</p>
        <A class="button button-primary" href="/app">Open dashboard <span>→</span></A>
      </Show>
      <Show when={state() === "checking"}>
        <h1>Finalising your access.</h1>
        <p>Payment completed successfully. We are securely confirming your subscription with Polar.</p>
        <div class="activation-status"><span class="spinner" /> Activating Pro membership…</div>
      </Show>
      <Show when={state() === "pending"}>
        <h1>Your payment was received.</h1>
        <p>Polar’s confirmation is taking a little longer than usual. Your access will activate automatically; refresh shortly or contact support if it remains unavailable.</p>
        <div class="success-actions"><button class="button button-primary" type="button" onClick={() => { setState("checking"); void checkMembership(); }}>Check again</button><a class="button button-quiet" href="mailto:support@ratingraces.pro">Contact support</a></div>
      </Show>
      <Show when={state() === "signed-out"}>
        <h1>Sign in to finish.</h1>
        <p>Your checkout completed, but this browser is not signed in. Use the same email address used for your Rating Races Pro account.</p>
        <A class="button button-primary" href="/login?next=success">Log in</A>
      </Show>
    </section>
  );
};

export default CheckoutSuccess;
